// -------------------------------
// BUDGET REVENUE INTERFACES
// -------------------------------
export interface PendingBudgetRevenue {
    revenueName?: string;
    categoryId?: number;
    budgetId?: number;
    amount?: string;
    revenueDate?: string; // YYYY-MM-DD
    status?: string;
    file?: File | null;
}

export interface PendingDeleteBudgetRevenue {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE BUDGET REVENUE — START
// -------------------------------
export const startCreateBudgetRevenueFlow = (
    setPending: (data: PendingBudgetRevenue | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPending({ revenueName: nameFromIntent });
        pushAIChat("Please select the category.");
        speak("Please select the category.");
        return;
    }

    setPending({});
    pushAIChat("What is the revenue name?");
    speak("What is the revenue name?");
};

// -------------------------------
// CREATE BUDGET REVENUE — PROCESS
// -------------------------------
export const processPendingBudgetRevenue = (
    text: string,
    pending: PendingBudgetRevenue,
    setPending: (data: PendingBudgetRevenue | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Revenue Name
    if (!pending.revenueName) {
        const name = text.trim();
        if (!name) {
            pushAIChat("Please provide a valid revenue name.");
            speak("Please provide a valid revenue name.");
            return;
        }

        setPending({ ...pending, revenueName: name });

        pushAIChat("Please select a category.");
        speak("Please select a category.");

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "budget-revenue-category" }
            })
        );
        return;
    }

    // STEP 2 — Category (wait for ai-option-selected)
    if (!pending.categoryId) {
        return;
    }

    // STEP 3 — Budget (ASK QUESTION ❗)
    if (!pending.budgetId) {
        pushAIChat("Please select a budget.");
        speak("Please select a budget.");

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "budget-revenue-budget" }
            })
        );
        return;
    }

    // STEP 4 — Amount
    if (!pending.amount) {
        const amount = text.replace(/[^0-9.]/g, "");
        if (!amount) {
            pushAIChat("Please provide a valid amount.");
            speak("Please provide a valid amount.");
            return;
        }

        setPending({ ...pending, amount });

        pushAIChat("Please provide the revenue date.");
        speak("Please provide the revenue date.");
        return;
    }

    // STEP 5 — Revenue Date (FINAL)
    if (!pending.revenueDate) {
        setPending({ ...pending, revenueDate: text });

        pushAIChat(`Creating budget revenue "${pending.revenueName}"...`);
        speak(`Creating budget revenue ${pending.revenueName}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-budget-revenue", {
                detail: {
                    ...pending,
                    revenueDate: text
                }
            })
        );

        setPending(null);
        return;
    }
};

// -------------------------------
// DELETE BUDGET REVENUE — START
// -------------------------------
export const startDeleteBudgetRevenueFlow = (
    setPendingDelete: (data: PendingDeleteBudgetRevenue | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting budget revenue "${nameFromIntent}"...`);
        speak(`Deleting budget revenue ${nameFromIntent}.`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-budget-revenue", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which budget revenue would you like to delete?");
    speak("Which budget revenue would you like to delete?");
};

// -------------------------------
// DELETE BUDGET REVENUE — PROCESS
// -------------------------------
export const processPendingDeleteBudgetRevenue = (
    text: string,
    pending: PendingDeleteBudgetRevenue,
    setPendingDelete: (data: PendingDeleteBudgetRevenue | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid budget revenue name.");
        speak("Please provide a valid budget revenue name.");
        return;
    }

    pushAIChat(`Deleting budget revenue "${cleanName}"...`);
    speak(`Deleting budget revenue ${cleanName}.`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-budget-revenue", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};


